import type BackOfficeConnector from "../data/backOfficeConnector";
import RepositoryBase from "./repositoryBase";
import type EventBus from "../services/eventBus";
import ExternalReportResponse from "entities/externalReportResponse";
import type ExternalReportType from "entities/externalReportType";
import { serializeFilters } from "utils/filters";
import CreateExternalReportResponse from "entities/createExternalReportResponse";
import type { AxiosPromise } from "axios";
import GetExternalReportDetailResponse from "entities/getExternalReportDetailResponse";

export default class ExternalReportsRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  get(id: string) {
    return this.callApi(api => api.all("reports").one("external_reports", id).getEntity(GetExternalReportDetailResponse));
  }

  getExternalReports(externalReportType: ExternalReportType) {
    return this.callApi(api =>
      api
        .all("reports")
        .all("external_reports")
        .getEntities(ExternalReportResponse, serializeFilters({ type: externalReportType }))
    );
  }

  generateNewCzechStatisticalOfficeReport(fromQuarter: string, toQuarter: string) {
    return this.callApi(api =>
      api
        .all("reports")
        .all("external_reports")
        .one("cso_report")
        .postEntity(
          {
            quarterOfYear: {
              from: fromQuarter,
              to: toQuarter,
            },
          },
          CreateExternalReportResponse
        )
    );
  }

  generateNewMinistryOfFinanceReport(fromDate: Date, toDate: Date) {
    return this.callApi(api =>
      api
        .all("reports")
        .all("external_reports")
        .one("mof_report")
        .postEntity(
          {
            reportDateRange: {
              from: fromDate,
              to: toDate,
            },
          },
          CreateExternalReportResponse
        )
    );
  }

  generateNewMinistryOfTransportReport(fromDate: Date, toDate: Date) {
    return this.callApi(api =>
      api
        .all("reports")
        .all("external_reports")
        .one("mot_report")
        .postEntity(
          {
            reportDateRange: {
              from: fromDate,
              to: toDate,
            },
          },
          CreateExternalReportResponse
        )
    );
  }

  downloadReport(id: string): AxiosPromise {
    return this.callApi(api => api.all("reports").one("external_reports", id).one("report").getRaw({ flow: "download" }) as any);
  }
}
